import React, {Fragment, useCallback, useEffect} from "react";
import {NavLink} from "react-router-dom";
import {NavItem} from "reactstrap";
import {observer} from "mobx-react-lite";
import {eventsRoute, IRoute, residentAdminRoute} from "../../routes";
import {useStores} from "../../stores/useStores";
import {useTranslation} from "react-i18next";
import "./navbarVerticalMenu.sass";
import MenuItem from "../../components/atoms/menu/menuItem";
import AdminMenuSection from "./navbarSections/adminMenuSection";
import HeadOfficeMenuSection from "./navbarSections/headOfficeMenuSection";
import DropdownInput from "../../components/atoms/input/dropdownInput";
import {useHistory, useLocation} from "react-router";
import {QueryParams} from "../../shared/enums/queryParams";
import {RoutePaths} from "../../shared/routePaths";
import {
  multiVillageKeyValue,
  ServicedApartmentResidentsFeatureName
} from "../../shared/constants";
import {AuthUser} from "../../utils/auth/user";
import {MultiVillageHelper} from "../../shared/multiVillageHelper";
import NavbarTitle from "./navbarTitle/navbarTitle";
import {Navigation} from "../../utils/navigation";
import {useVillageFeatureToggle} from "../../utils/featureToggle/villageFeatureToggle";
import NavbarCalendarsMenu from "../../components/organisms/navbarCalendarsMenu/navbarCalendarsMenu";
import NavbarResidentsMenu from "../../components/organisms/navbarResidentsMenu/navbarResidentsMenu";
import {KeyValue} from "../../stores/observables/keyValue";

export interface INavbarVerticalMenuProps {
  routes?: IRoute[];
}

const NavbarVerticalMenu: React.FC<INavbarVerticalMenuProps> = ({
  routes = []
}) => {
  const {
    uiStore: {
      eventsUiStore,
      noticesUiStore,
      independentResidentAdminUiStore,
      accountStore
    },
    uiStore
  } = useStores();

  const {t} = useTranslation(["translation"]);

  const disableVillageSelector =
    eventsUiStore.isCreatingOrEditing ||
    eventsUiStore.isViewingEvent ||
    noticesUiStore.isCreatingOrEditing ||
    independentResidentAdminUiStore.isCreatingOrEditing ||
    noticesUiStore.isViewingNotice;

  const history = useHistory();
  const location = useLocation();
  useEffect(() => {
    uiStore.resetAllState();
  }, [location.pathname, uiStore]);

  const search = location.search;
  const queryParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const villageId = queryParams.get(QueryParams.VillageId);
  const villageIdQueryParam = `?${QueryParams.VillageId}=${villageId}`;

  useEffect(() => {
    if (villageId) {
      if (uiStore.selectableVillages.items.find((x) => x.value === villageId)) {
        uiStore.selectableVillages.setSelectedItem(villageId);
      } else {
        // We don't know about this village id so the best we can do is send the user back to the home page
        history.push(RoutePaths.Home);
      }
    }
  }, [queryParams, uiStore.selectableVillages, history, villageId]);

  const isOnEventCalendarsPage = location.pathname === eventsRoute.to;
  const isOnCreateEventPage = location.pathname === RoutePaths.CreateEvent;
  const isOnViewOrEditEventPage =
    location.pathname.slice(0, location.pathname.lastIndexOf("/")) ===
    RoutePaths.EditEvent.slice(0, RoutePaths.EditEvent.lastIndexOf("/"));

  const hasServicedApartmentResidents = useVillageFeatureToggle(
    ServicedApartmentResidentsFeatureName
  ).hasFeature.value;

  const selectedCalendar = decodeURI(
    queryParams.get(QueryParams.calendar) || ""
  );
  const selectedVillageId = uiStore.selectableVillages.selectedItem?.value;
  useEffect(() => {
    if (
      selectedVillageId &&
      (!villageId || villageId === selectedVillageId) &&
      !hasServicedApartmentResidents &&
      location.pathname.replace(/\/+$/, "") ===
        RoutePaths.ServicedApartmentResidentAdmin
    ) {
      history.replace({
        ...location,
        pathname: RoutePaths.IndependentResidentAdmin
      });
    }
  }, [
    selectedVillageId,
    villageId,
    hasServicedApartmentResidents,
    location,
    history
  ]);

  const calendars =
    selectedVillageId === multiVillageKeyValue.value
      ? MultiVillageHelper.multiVillageVillage.calendars
      : AuthUser.getInstance().Villages?.find((v) => v.id === selectedVillageId)
          ?.calendars;
  useEffect(() => {
    if (calendars && calendars.length > 0) {
      const defaultCalendarName = calendars[0]?.name || "";
      const isValidCalendar = !!calendars?.find(
        (calendar) => calendar.name === selectedCalendar
      );
      if (isOnEventCalendarsPage && !isValidCalendar && defaultCalendarName) {
        Navigation.navigateWithParams(
          history,
          RoutePaths.Events,
          QueryParams.calendar,
          encodeURI(defaultCalendarName),
          [QueryParams.type]
        );
      }
    }
  }, [calendars, history, isOnEventCalendarsPage, selectedCalendar]);

  const updateVillageInQueryParam = useCallback(
    (villageId: KeyValue) => {
      queryParams.delete(QueryParams.VillageId);
      queryParams.append(QueryParams.VillageId, villageId.value);
      history.push({
        ...history.location,
        search: queryParams.toString()
      });
    },
    [queryParams, history]
  );

  // TODO: This effect disables/enables MV option based on uri and admin access level.
  //  Remove once fully released
  useEffect(() => {
    if (accountStore.abilities.can("Create", "MultiVillage")) {
      const currentPageUri = "/" + location.pathname.split("/")[1]; // Get the current page. eg: /calendar
      switch (currentPageUri) {
        case RoutePaths.ResidentAdmin: // always hide MV option for residents
          MultiVillageHelper.ShowOrHideMultiVillageOption(uiStore, false);
          break;
        default:
          MultiVillageHelper.ShowOrHideMultiVillageOption(uiStore, true);
          break;
      }
    }
  }, [location]);

  return (
    <div className="navbar-vertical-menu">
      <HeadOfficeMenuSection />
      <AdminMenuSection />
      {uiStore.selectableVillages?.items.length && (
        <>
          <NavbarTitle
            title={t("translation:labels.village", {
              count: uiStore.selectableVillages?.items.length
            })}
          />

          {uiStore.selectableVillages.items.length > 1 ? (
            <DropdownInput
              value={uiStore.selectableVillages}
              placeholder={t("translation:controls.villageSelect.placeholder")}
              disabled={disableVillageSelector}
              onChange={updateVillageInQueryParam}
              inputId="village-selector-input"
            />
          ) : (
            <span>{uiStore.selectableVillages.selectedItem?.label}</span>
          )}
        </>
      )}
      {uiStore.selectableVillages?.selectedItem && (
        <>
          <NavbarTitle title={t("translation:labels.menu")} />
          {routes.map((route, index) => {
            return (
              <Fragment key={index}>
                <NavItem tag={"div"}>
                  {route.name === eventsRoute.name ? (
                    <NavbarCalendarsMenu
                      calendars={calendars}
                      initialIsExpanded={
                        isOnEventCalendarsPage ||
                        isOnCreateEventPage ||
                        isOnViewOrEditEventPage
                      }
                      selectedCalendar={selectedCalendar}
                    />
                  ) : route.name === residentAdminRoute.name ? (
                    hasServicedApartmentResidents ? (
                      <NavbarResidentsMenu
                        permanentlyExpanded
                        villageId={selectedVillageId}
                        currentPathName={location.pathname}
                      />
                    ) : (
                      <NavLink
                        className="nav-link"
                        to={
                          RoutePaths.IndependentResidentAdmin +
                          `?${QueryParams.VillageId}=${selectedVillageId}`
                        }
                      >
                        <MenuItem
                          title={t(residentAdminRoute.name)}
                          icon={residentAdminRoute.icon}
                        />
                      </NavLink>
                    )
                  ) : (
                    <NavLink
                      className="nav-link"
                      to={route.to + villageIdQueryParam}
                      exact={route.exact}
                    >
                      <MenuItem title={t(route.name)} icon={route.icon} />
                    </NavLink>
                  )}
                </NavItem>
              </Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};
export default observer(NavbarVerticalMenu);
